// Backend API client (Cloudflare Worker `kaden-onboarding-api`).
// Faithfully reconstructed from the deployed build — same base URL, auth
// scheme, endpoints, and return shapes.

import { getIdToken } from "./auth";

// The deployed frontend calls the Worker directly at its workers.dev origin.
// Override via VITE_API_BASE for local/custom-domain setups.
const API_BASE = import.meta.env.VITE_API_BASE ?? "https://kaden-onboarding-api.anabil.workers.dev";
const TIMEOUT_MS = 30000;

const ADMIN_TOKEN_KEY = "kd_admin_token_v1";

export function apiConfigured(): boolean {
  return !!API_BASE;
}
export function apiBase(): string {
  return API_BASE;
}

export function getAdminToken(): string | null {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setAdminToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function adminAuthHeader(): Record<string, string> {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
async function msalAuthHeader(): Promise<Record<string, string>> {
  try {
    const token = await getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export interface RequestOptions extends RequestInit {
  authAs?: "msal" | "admin";
}

async function buildHeaders(opts?: RequestOptions): Promise<Record<string, string>> {
  return {
    "Content-Type": "application/json",
    ...(opts?.authAs === "msal" ? await msalAuthHeader() : adminAuthHeader()),
    ...((opts?.headers as Record<string, string>) || {}),
  };
}

export interface ApiResult<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
  error?: string;
}

/** Full-result request — returns { ok, status, data, error }. */
export async function apiRequest<T = any>(path: string, opts?: RequestOptions): Promise<ApiResult<T>> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    const res = await fetch(API_BASE.replace(/\/$/, "") + path, { ...opts, headers: await buildHeaders(opts), signal: controller.signal });
    clearTimeout(timer);
    const data = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, data, error: (data as any)?.error };
  } catch (err) {
    return { ok: false, status: 0, data: null, error: err instanceof Error ? err.message : String(err) };
  }
}

/** JSON-or-null request — returns parsed body on 2xx, else null. */
export async function apiJson<T = any>(path: string, opts?: RequestOptions): Promise<T | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    const res = await fetch(API_BASE.replace(/\/$/, "") + path, { ...opts, headers: await buildHeaders(opts), signal: controller.signal });
    clearTimeout(timer);
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

// --- Onboarding telemetry / sessions (authenticated as the MSAL user) ---

export function postEvent(payload: unknown) {
  return apiJson("/api/events", { method: "POST", body: JSON.stringify(payload), authAs: "msal" });
}

export function createSession(payload: unknown) {
  return apiJson("/api/sessions", { method: "POST", body: JSON.stringify(payload), authAs: "msal" });
}

export function updateSession(id: string, patch: unknown) {
  return apiJson(`/api/sessions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch), authAs: "msal" });
}

export async function getSessions() {
  return (await apiJson<{ sessions?: unknown[] }>("/api/sessions", { method: "GET" }))?.sessions ?? null;
}

export function postAudit(payload: unknown) {
  return apiJson("/api/audit", { method: "POST", body: JSON.stringify(payload) });
}

// --- Q&A (department FAQ) ---

export async function getQA<T = unknown>() {
  return (await apiJson<{ items?: T[] }>("/api/qa", { method: "GET" }))?.items ?? null;
}
export function putQA(items: unknown[]) {
  return apiJson("/api/qa", { method: "PUT", body: JSON.stringify({ items }) });
}

// --- Email / notifications ---

export function sendEmail(payload: unknown) {
  return apiJson("/api/send-email", { method: "POST", body: JSON.stringify(payload) });
}

export interface NotifyArgs {
  to: string;
  lang?: string;
  vars?: Record<string, unknown>;
  attachments?: unknown[];
}
export async function notify(templateKey: string, args: NotifyArgs): Promise<{ ok: boolean; error?: string } & Record<string, unknown>> {
  const res = await apiRequest("/api/notify", {
    method: "POST",
    authAs: "msal",
    body: JSON.stringify({ templateKey, to: args.to, lang: args.lang, vars: args.vars, attachments: args.attachments }),
  });
  return (res.data as any) ?? { ok: false, error: res.error || `HTTP ${res.status}` };
}

export function sendReminders(emails: string[]) {
  return apiJson("/api/admin/reminders/send", { method: "POST", body: JSON.stringify({ emails }) });
}

// --- Settings & public config ---

export function getSetting<T = any>(key: string) {
  return apiRequest<T>(`/api/settings/${key}`, { method: "GET" });
}
export function putSetting(key: string, value: unknown) {
  return apiRequest(`/api/settings/${key}`, { method: "PUT", body: JSON.stringify({ value }) });
}
export function getPublic<T = any>(key: string) {
  return apiRequest<T>(`/api/public/${key.replace(/_/g, "-")}`, { method: "GET" });
}

// --- Admin authentication ---

export interface AdminAuthResult {
  ok: boolean;
  token?: string;
  expiresAt?: number;
  name?: string;
  email?: string;
  error?: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  createdAt: number;
}

export async function adminExists(): Promise<boolean> {
  return !!(await apiRequest<{ exists: boolean }>("/api/admin/exists", { method: "GET" })).data?.exists;
}

export async function adminSetup(name: string, email: string, password: string): Promise<AdminAuthResult> {
  const res = await apiRequest<{ token?: string; expiresAt?: number; name?: string; email?: string }>("/api/admin/setup", {
    method: "POST",
    body: JSON.stringify({ name, email, password }),
  });
  if (res.ok && res.data?.token) {
    setAdminToken(res.data.token);
    return { ok: true, token: res.data.token, expiresAt: res.data.expiresAt, name: res.data.name, email: res.data.email };
  }
  return { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminLogin(email: string, password: string): Promise<AdminAuthResult> {
  const res = await apiRequest<{ token?: string; expiresAt?: number; name?: string; email?: string }>("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (res.ok && res.data?.token) {
    setAdminToken(res.data.token);
    return { ok: true, token: res.data.token, expiresAt: res.data.expiresAt, name: res.data.name, email: res.data.email };
  }
  return { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminLogout(): Promise<void> {
  if (getAdminToken()) await apiRequest("/api/admin/logout", { method: "POST" });
  setAdminToken(null);
}

export async function adminMe(): Promise<{ ok: boolean; id?: string; name?: string; email?: string }> {
  const res = await apiRequest<{ ok?: boolean; id?: string; name?: string; email?: string }>("/api/admin/me", { method: "GET" });
  return res.ok && res.data?.ok ? { ok: true, id: res.data.id, name: res.data.name, email: res.data.email } : { ok: false };
}

export async function adminChangePassword(oldPassword: string, newPassword: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean }>("/api/admin/change-password", {
    method: "POST",
    body: JSON.stringify({ oldPassword, newPassword }),
  });
  return res.ok && res.data?.ok ? { ok: true } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminForgotPassword(email: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean }>("/api/admin/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
  return res.ok ? { ok: true } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminResetPassword(email: string, code: string, newPassword: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean }>("/api/admin/reset-password", {
    method: "POST",
    body: JSON.stringify({ email, code, newPassword }),
  });
  return res.ok && res.data?.ok ? { ok: true } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminListUsers(): Promise<{ ok: boolean; users: AdminUser[]; error?: string }> {
  const res = await apiRequest<{ ok?: boolean; users?: AdminUser[] }>("/api/admin/users", { method: "GET" });
  return res.ok && res.data?.ok ? { ok: true, users: res.data.users || [] } : { ok: false, users: [], error: res.error || `HTTP ${res.status}` };
}

export async function adminCreateUser(name: string, email: string, password: string): Promise<{ ok: boolean; user?: AdminUser; error?: string }> {
  const res = await apiRequest<{ ok?: boolean; user?: AdminUser }>("/api/admin/users", {
    method: "POST",
    body: JSON.stringify({ name, email, password }),
  });
  return res.ok && res.data?.ok ? { ok: true, user: res.data.user } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminUpdateUser(id: string, name: string, email: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean }>(`/api/admin/users/${id}`, { method: "PUT", body: JSON.stringify({ name, email }) });
  return res.ok && res.data?.ok ? { ok: true } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminDeleteUser(id: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean }>(`/api/admin/users/${id}`, { method: "DELETE" });
  return res.ok && res.data?.ok ? { ok: true } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export function adminActivity() {
  return apiRequest("/api/admin/activity", { method: "GET" });
}

/** Clears an address's send history so welcome/completion/manager emails —
 *  permanently deduped per (employee, template) in production — can be
 *  re-sent to it. Mainly for the admin's own testing runs. */
export function clearEmailLog(email: string) {
  return apiRequest<{ ok?: boolean; cleared?: number; error?: string }>("/api/admin/email-log/clear", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export interface HandbookFile {
  filename: string;
  contentType: string;
  size: number;
  uploadedAt: number;
  uploadedBy?: string;
}

/** Current handbook metadata (no file bytes), or null if none uploaded yet. */
export async function adminGetHandbook(): Promise<{ ok: boolean; file: HandbookFile | null; error?: string }> {
  const res = await apiRequest<{ ok?: boolean; file?: HandbookFile | null }>("/api/admin/handbook", { method: "GET" });
  return res.ok && res.data?.ok ? { ok: true, file: res.data.file ?? null } : { ok: false, file: null, error: res.error || `HTTP ${res.status}` };
}

/** Uploads/replaces the employee handbook (base64-encoded file content). */
export async function adminUploadHandbook(filename: string, contentType: string, contentBase64: string): Promise<{ ok: boolean; file?: HandbookFile; error?: string }> {
  const res = await apiRequest<{ ok?: boolean; file?: HandbookFile }>("/api/admin/handbook", {
    method: "POST",
    body: JSON.stringify({ filename, contentType, contentBase64 }),
  });
  return res.ok && res.data?.ok ? { ok: true, file: res.data.file } : { ok: false, error: res.error || `HTTP ${res.status}` };
}

export async function adminDeleteHandbook(): Promise<{ ok: boolean; error?: string }> {
  const res = await apiRequest<{ ok?: boolean }>("/api/admin/handbook", { method: "DELETE" });
  return res.ok && res.data?.ok ? { ok: true } : { ok: false, error: res.error || `HTTP ${res.status}` };
}
